import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { queryParam } from 'teacher-ui-client/decorators/query-param'
import {
  serialize as serializeSort,
  deserialize as deserializeSort,
  updateSortColumns,
} from '@blakeelearning/data-tables/utils/sorting'
import type { SortingConfig } from '@blakeelearning/data-tables/utils/sorting'
import { getSortViewModel } from 'teacher-ui-client/utils/sorting'
import { t, formatDate } from 'ember-intl'
import { on } from '@ember/modifier'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import type { BaseReport, BaseReportMetaPaging } from 'teacher-ui-client/services/reporting'
import Tooltip from 'teacher-ui-client/components/tooltip'
import { and } from 'ember-truth-helpers'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { join } from 'teacher-ui-client/utils/uri'
import config from 'teacher-ui-client/config/environment'

interface StudentWord {
  student_id: string
  student_blake_id: number
  word: Nullable<string>
  student_usage: Nullable<string>
  activity_result_snapshot_id: Nullable<string>
  activity_id: string
  created_date: Nullable<string>
  first_name: string
  last_name: string
  grade_position: number
  template: Nullable<string>
}

export interface VocabularyStudentWordsUsage extends BaseReport {
  student_words: StudentWord[]
  meta: BaseReportMetaPaging
}

interface Signature {
  Element: HTMLTableElement
  Args: {
    isReloading?: boolean
    infoTooltip?: string
    vocabularyData?: VocabularyStudentWordsUsage
  }
}

function viewLink(studentWord: StudentWord): string {
  if (!studentWord.activity_result_snapshot_id) {
    return ''
  }

  return join(
    config.readingEggsBaseUrl,
    'writing/teacher_portal',
    studentWord.template === 'long-write' ? 'long-writes' : 'exciting-writing-ideas',
    studentWord.activity_id,
    'students',
    studentWord.student_id,
    'submissions',
    studentWord.activity_result_snapshot_id,
  ).href
}

function splitOn(str: string | null, delimiter: string): string[] {
  if (!str) return []
  return str.split(delimiter)
}

function shouldHighlight(wordFromUsage: string, words: string | null): boolean {
  if (!words) return false
  const wordsArray = words.split(',')
  for (let i = 0; i < wordsArray.length; i++) {
    const word = wordsArray[i].trim()
    const re = new RegExp(`\\b${word}\\b`, 'gi')
    if (re.test(wordFromUsage)) {
      return true
    }
  }
  return false
}

export const VocabularyUsageTable: TOC<Signature> = <template>
  <table class="divide-dusty-black-200 relative table-fixed divide-y" ...attributes>
    <thead>
      <tr class="bg-white">
        <ThSort class="min-w-32" @sortKey="first_name" @text={{t "reporting.firstName"}} />
        <ThSort class="min-w-32" @sortKey="last_name" @text={{t "reporting.lastName"}} />
        <ThSort @sortKey="created_date" @text={{t "pages.wlVocabulary.when"}} />
        <Th @text={{t "pages.wlVocabulary.words"}} />
        <Th @text={{t "pages.wlVocabulary.studentUsage"}} />
        <th class="text-right">
          {{#if @infoTooltip}}
            <span class="px-4 print:hidden">
              <PhosphorIcon @icon="phosphor-info-fill" class="text-dusty-black-500 h-4 w-4 cursor-pointer" />
              <Tooltip @text={{@infoTooltip}} />
            </span>
          {{/if}}
        </th>
      </tr>
    </thead>
    <tbody class="divide-dusty-black-200 divide-y {{if @isReloading 'opacity-50'}}">
      {{#each @vocabularyData.student_words as |studentWord index|}}
        <tr class="bg-white" data-test-table-row="{{index}}">
          <td class="truncate p-3" data-test-cell="first_name">
            {{studentWord.first_name}}
          </td>
          <td class="truncate p-3" data-test-cell="last_name">
            {{studentWord.last_name}}
          </td>
          <td class="p-3" data-test-cell="created_date">
            {{if
              studentWord.created_date
              (formatDate studentWord.created_date day="numeric" month="short" year="numeric")
              "-"
            }}
          </td>
          <td class="p-3" data-test-cell="word">
            {{formatValue studentWord.word}}
          </td>
          <td class="p-3" data-test-cell="student_usage">
            {{#if (and studentWord.student_usage studentWord.word)}}
              {{#each (splitOn studentWord.student_usage " ") as |usageWord|}}
                <span
                  after=" "
                  class="inline-block after:relative after:content-[attr(after)] last:after:hidden
                    {{if (shouldHighlight usageWord studentWord.word) 'font-bold'}}"
                >{{usageWord}}</span>
              {{/each}}
            {{/if}}
          </td>
          <td class="p-3">
            {{#if studentWord.activity_result_snapshot_id}}
              <a data-test-view-link class="text-center" href={{viewLink studentWord}}>
                <div><PhosphorIcon @icon="phosphor-file-text-fill" /> {{t "pages.recentWriting.card.viewLink"}}</div>
              </a>
            {{/if}}
          </td>
        </tr>
      {{/each}}
    </tbody>
  </table>
</template>

interface ThSortSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
    sortKey: string
  }
}

class ThSort extends Component<ThSortSignature> {
  @queryParam('reporting/ui-scope/writing-legends/vocabulary/bank') sort!: string

  get sortingConfig() {
    return deserializeSort(this.sort)
  }

  set sortingConfig(newSortConfig: SortingConfig) {
    this.sort = serializeSort(newSortConfig)
  }

  updateSort = () => {
    if (this.args.sortKey) this.sortingConfig = updateSortColumns(this.sortingConfig, this.args.sortKey)
  }

  get sortViewModel() {
    return getSortViewModel(this.sortingConfig, this.args.sortKey)
  }

  <template>
    <th
      class="h-[80px] select-none"
      data-test-sort-key={{@sortKey}}
      aria-sort={{this.sortViewModel.ariaDirection}}
      ...attributes
    >
      <button
        type="button"
        class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}"
        {{on "click" this.updateSort}}
      >
        {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
        <PhosphorIcon @icon={{this.sortViewModel.icon}} class="text-dusty-black-400" />
      </button>
    </th>
  </template>
}

interface ThSignature {
  Element: HTMLTableCellElement
  Args: {
    text?: string
  }
}

const Th: TOC<ThSignature> = <template>
  <th class="h-[80px] select-none" ...attributes>
    <div class="flex h-full w-full items-center gap-x-3 p-3 text-left {{if @text 'justify-between' 'justify-end'}}">
      {{#if @text}}<span class="text-dusty-black-250 font-normal uppercase">{{@text}}</span>{{/if}}
    </div>
  </th>
</template>
